import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTenant } from '@/contexts/tenant-context';
import { usePatchTenantsByIdMutation, useDeleteTenantsByIdMutation } from '@/services';
import { toast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  type: z.enum(['personal', 'business', 'enterprise']),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function TenantSettings() {
  const { currentTenant, refreshTenants } = useTenant();
  const [updateTenant, { isLoading: isUpdating }] = usePatchTenantsByIdMutation();
  const [deleteTenant, { isLoading: isDeleting }] = useDeleteTenantsByIdMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: currentTenant?.name || '',
      type: (currentTenant?.type as 'personal' | 'business' | 'enterprise') || 'personal',
      description: currentTenant?.description || '',
    },
  });

  // Update form when tenant changes
  useEffect(() => {
    if (currentTenant) {
      form.reset({
        name: currentTenant.name,
        type: currentTenant.type as 'personal' | 'business' | 'enterprise',
        description: currentTenant.description || '',
      });
    }
  }, [currentTenant, form]);

  const onSubmit = async (data: FormValues) => {
    if (!currentTenant) return;

    try {
      await updateTenant({
        id: currentTenant.id,
        body: {
          name: data.name,
          type: data.type,
          description: data.description || null,
        },
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Tenant settings updated successfully',
      });

      refreshTenants();
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to update tenant settings',
      });
    }
  };

  const handleDeleteTenant = async () => {
    if (!currentTenant) return;

    try {
      await deleteTenant({ id: currentTenant.id }).unwrap();

      toast({
        title: 'Success',
        description: 'Tenant deleted successfully',
      });

      refreshTenants();
      setShowDeleteDialog(false);
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to delete tenant',
      });
    }
  };

  if (!currentTenant) {
    return <div className="text-center py-10">Please select a tenant first</div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Tenant Settings</CardTitle>
          <CardDescription>Manage your tenant preferences and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tenant Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tenant Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select tenant type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card className="mt-6 border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="text-red-500">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions that can't be undone</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Deleting this tenant will remove all associated data, including devices, locations, and user permissions.
            This action cannot be undone.
          </p>

          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Tenant
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the tenant "{currentTenant.name}" and all
              associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTenant}
              className="bg-red-500 hover:bg-red-600"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
